/**
 * What a Suggestion Engine strategy actually DOES — the apportionment and the three levers, on the
 * card, against the baseline.
 *
 * OWNER-CAUGHT (2026-08-26): "it shows selling the home but it does not clearly provide details of
 * exactly how the balance is apportioned — it's very generic in its explanation and this is not good
 * enough. It should clearly explain the strategy and give context again against the levers of age
 * pension, cashflow, legacy against baseline."
 *
 * The card used to carry a paragraph of description and a one-line "How it's funded" that named only
 * the entry contribution. Selling an $800,000 home to pay an $800,000 entry contribution is a
 * complete story; selling a $2,000,000 home to pay it is not, and the missing $1.2m is the part the
 * adviser has to explain to the client. So this component answers two questions in order:
 *
 *   1. WHERE DOES THE MONEY GO — every dollar of the sale, from gross price down to what is left in
 *      the bank, as a ledger. The destinations come from the candidate's OWN inputs (the same object
 *      the engine projected), so the ledger and the projection can never describe different plans.
 *   2. WHAT DOES IT DO — Age Pension, cashflow and estate, each as baseline → this strategy with a
 *      driver-named reason. Every figure is the strategy's own snapshot or the baseline's, quoted;
 *      nothing here is re-derived.
 *
 * CALCULATION DISCIPLINE: this component performs NO financial modelling. It reads allocations the
 * adviser or the engine already chose and figures the engine already computed. The one arithmetic it
 * does is the remainder (proceeds less the named allocations), which is definitionally what is left.
 */
import { SUGGESTED_PROPERTY_COSTS_WORDS } from '../../../../shared/src/calculation/support/investmentPropertyDefaults.ts';
import React from 'react';
import { formatCurrency } from '../../utils/index.ts';
import { LedgerRow, ShiftRow } from '../../components/narrative/PlainWords.tsx';
import type { RvEvaluatedStrategy } from './RetirementVillagePage.tsx';
import { ASSET_CLASS_ASSUMPTIONS } from '../../../../shared/src/calculation/investmentProfiles.ts';

export interface RvStrategyExplainerProps {
    strategy: RvEvaluatedStrategy;
    /** The stay-put baseline every strategy is measured against. */
    baseline: { snapshot?: { pension?: number; netCashflow?: number; legacyValue?: number } } | null | undefined;
    entryFee: number;
    legacyYears: number;
    /** Names for the two members, for the downsizer rows. */
    clientName?: string;
    partnerName?: string;
    /**
     * Whether each member is under Age Pension age today, so the downsizer rows say what the means
     * tests DO with the money for this household rather than "counted unless…" (owner, 24 Sep 2026).
     */
    clientUnderPensionAge?: boolean;
    partnerUnderPensionAge?: boolean;
}

/** The chip on a downsizer row: super in accumulation is exempt only while its owner is under Age Pension age. */
export const superChipFor = (name: string, underPensionAge: boolean | undefined): string =>
    underPensionAge === true
        ? `not counted while ${name} is under Age Pension age`
        : underPensionAge === false
            ? `counted — ${name} is over Age Pension age`
            : 'counted unless the owner is under Age Pension age';

const amountOf = (value: unknown): number => {
    const parsed = parseFloat(String(value ?? ''));
    return Number.isFinite(parsed) ? parsed : 0;
};

const RvStrategyExplainer = ({
    strategy,
    baseline,
    entryFee,
    legacyYears,
    clientName = 'you',
    partnerName = 'your partner',
    clientUnderPensionAge,
    partnerUnderPensionAge,
}: RvStrategyExplainerProps) => {
    const inputs = (strategy?.inputs || {}) as Record<string, any>;
    const fundFrom: string = inputs.fundFrom || 'liquid';
    const details = (inputs.sellHomeDetails || {}) as Record<string, any>;

    // ---- 1. Where the money goes -------------------------------------------------------------
    // Only a PROPERTY sale has a remainder worth itemising; a savings-funded move spends the entry
    // contribution out of accounts the "How it's funded" list already names one by one.
    const salePrice = amountOf(details.salePrice);
    const sellingCosts = amountOf(details.sellingCosts);
    const netProceeds = Math.max(0, salePrice - sellingCosts);
    const showLedger = fundFrom === 'sell_home' && netProceeds > 0;

    const allocations = showLedger
        ? ([
              { label: 'Village entry contribution', amount: Math.min(entryFee, netProceeds), chip: 'refundable on exit, less the departure fee' as React.ReactNode },
              { label: `Into super — downsizer contribution (${clientName})`, amount: amountOf(details.downsizerContribution), chip: superChipFor(clientName, clientUnderPensionAge) as React.ReactNode },
              { label: `Into super — downsizer contribution (${partnerName})`, amount: amountOf(details.partnerDownsizerContribution), chip: superChipFor(partnerName, partnerUnderPensionAge) as React.ReactNode },
              { label: 'Lifetime annuity', amount: amountOf(details.purchaseAnnuity), chip: 'only 60% counted in the assets test' as React.ReactNode },
              { label: 'Shares / managed funds', amount: amountOf(details.purchaseShares), chip: 'counted and deemed' as React.ReactNode },
              { label: 'Investment property', amount: amountOf(details.purchaseInvestmentProperty), chip: 'counted; rent is assessable' as React.ReactNode },
              { label: 'Stamp duty on the property', amount: amountOf(details.purchaseInvestmentProperty) > 0 ? amountOf(details.investmentPropertyStampDuty) : 0, chip: 'paid to the state; part of the cost base' as React.ReactNode },
              { label: 'Gift to family', amount: amountOf(details.giftingAmount), chip: 'still counted for 5 years above the limits' as React.ReactNode },
              { label: 'Funeral bond', amount: amountOf(details.funeralBondPurchase), chip: 'exempt within the limit' as React.ReactNode },
          ].filter((row) => row.amount > 0))
        : [];
    const allocated = allocations.reduce((sum, row) => sum + row.amount, 0);
    const remainder = Math.max(0, netProceeds - allocated);

    // ---- 2. What it does, against the baseline -----------------------------------------------
    const basePension = baseline?.snapshot?.pension ?? 0;
    const baseCashflow = baseline?.snapshot?.netCashflow ?? 0;
    const baseLegacy = baseline?.snapshot?.legacyValue ?? 0;
    const pension = strategy?.snapshot?.pension ?? 0;
    const cashflow = strategy?.snapshot?.netCashflow ?? 0;
    const legacy = strategy?.snapshot?.legacyValue ?? 0;
    const hasBaseline = !!baseline?.snapshot;

    /*
     * The reasons name THIS strategy's own drivers, from its own inputs — never a generic list of
     * everything that can move a pension. A cause the reader can check against the ledger above is
     * the difference between an explanation and a disclaimer.
     */
    const pensionWhy = (() => {
        if (fundFrom === 'sell_home') {
            const shelters = [
                amountOf(details.downsizerContribution) + amountOf(details.partnerDownsizerContribution) > 0 ? 'super' : null,
                amountOf(details.purchaseAnnuity) > 0 ? 'an annuity (60% counted)' : null,
                amountOf(details.funeralBondPurchase) > 0 ? 'a funeral bond' : null,
            ].filter(Boolean) as string[];
            return `The home stops being an exempt asset and becomes money Centrelink counts. The entry contribution itself drops out of the assets test${
                shelters.length > 0 ? `, and ${shelters.join(' and ')} shelter part of the rest` : ''
            }${remainder > 0 ? `, but ${formatCurrency(remainder)} stays in the bank where it is counted and deemed` : ''}.`;
        }
        if (fundFrom === 'investment_property') {
            return 'Selling the investment property removes its rent from the income test, but turns its value into counted money — less whatever capital gains tax the sale triggers.';
        }
        if (inputs.rentHome) {
            return 'The family home is kept, so it is counted in full once the village unit becomes the principal home — a village entry carries no two-year exemption. The rent is assessable income on top.';
        }
        return 'Paying the entry contribution out of savings REDUCES counted assets, because money spent on the village unit is not assessed. Whatever the home does then decides the rest.';
    })();

    const cashflowWhy = (() => {
        const parts: string[] = [];
        if (pension !== basePension) parts.push(`the Age Pension ${pension < basePension ? 'falls' : 'rises'} by ${formatCurrency(Math.abs(pension - basePension))}`);
        if ((strategy.annuityIncome ?? 0) > 0) parts.push(`the annuity pays ${formatCurrency(strategy.annuityIncome ?? 0)} a year for life`);
        if (inputs.rentHome) parts.push('rent comes in from the former home');
        if (fundFrom === 'sell_home' || fundFrom === 'investment_property') parts.push('the money that used to be property now earns investment income instead');
        parts.push('village fees are an ongoing cost either way');
        return `${parts.join('; ')}.`;
    })();

    const legacyWhy = `What is left after ${legacyYears} ${legacyYears === 1 ? 'year' : 'years'}, after the departure fee is taken from the entry contribution and the rest of the money has grown or been drawn on to cover any shortfall.`;

    if (!showLedger && !hasBaseline) return null;

    return (
        <div className="mt-3 space-y-3">
            {showLedger && (
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Where the money goes</p>
                    <LedgerRow label="Sale of the home" amount={salePrice} />
                    {sellingCosts > 0 && <LedgerRow label="Selling costs" amount={sellingCosts} negative muted />}
                    <LedgerRow label="Net proceeds" amount={netProceeds} bold divider />
                    {allocations.map((row) => (
                        <LedgerRow key={row.label} label={row.label} amount={row.amount} negative indent chip={row.chip} chipTone="good" />
                    ))}
                    <LedgerRow
                        label="Left in the bank"
                        amount={remainder}
                        bold
                        divider
                        chip={remainder > 0 ? 'counted and deemed' : undefined}
                        chipTone="warn"
                    />
                    {/* The assumptions an investment property rests on, said where it is bought
                        (owner, 24 Sep 2026: "no guidance on the assumed growth and rental return"). */}
                    {amountOf(details.purchaseInvestmentProperty) > 0 && (
                        <p className="mt-2 text-xs leading-relaxed text-slate-600">
                            <span className="font-semibold text-slate-800">Investment property assumptions:</span> rent of{' '}
                            {ASSET_CLASS_ASSUMPTIONS.propertyYieldPct}% of value a year ({formatCurrency(amountOf(details.investmentPropertyRent))}),{' '}
                            {SUGGESTED_PROPERTY_COSTS_WORDS}, and growth of {ASSET_CLASS_ASSUMPTIONS.propertyGrowthPct}% a
                            year &mdash; the tool&apos;s standard assumptions.{' '}
                            {amountOf(details.investmentPropertyStampDuty) > 0
                                ? `Stamp duty of ${formatCurrency(amountOf(details.investmentPropertyStampDuty))}${details.investmentPropertyState ? ` (${details.investmentPropertyState} scale, no concessions)` : ''} comes out of the proceeds and sits in the property's cost base.`
                                : 'Stamp duty on the purchase is not modelled — no state was chosen for the purchase.'}
                        </p>
                    )}
                </div>
            )}

            {hasBaseline && (
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Against staying in your current home
                    </p>
                    <ShiftRow label="Age Pension" before={basePension} after={pension}>
                        <span className="text-xs text-slate-500">a year</span>
                    </ShiftRow>
                    <p className="-mt-1 mb-1 text-xs leading-relaxed text-slate-600">{pensionWhy}</p>
                    <ShiftRow label="Cashflow (year 1)" before={baseCashflow} after={cashflow} />
                    <p className="-mt-1 mb-1 text-xs leading-relaxed text-slate-600">{cashflowWhy}</p>
                    <ShiftRow label={`Estate (year ${legacyYears})`} before={baseLegacy} after={legacy} suffix="" />
                    <p className="-mt-1 text-xs leading-relaxed text-slate-600">{legacyWhy}</p>
                </div>
            )}
        </div>
    );
};

export default RvStrategyExplainer;
